import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How do I sign up to become a Speedyssey delivery partner?',
    answer:
      'Download the Speedyssey Driver App, submit your basic information and documents, and our team will guide you through verification.',
  },
  {
    question: 'When and how do I get paid?',
    answer: 'Earnings are deposited to your linked bank account daily. You can track your balance in the app in real-time.',
  },
  {
    question: 'How are delivery requests assigned?',
    answer:
      'Requests are dispatched based on your proximity, rating, and availability status. You will always see the estimated payout before accepting.',
  },
  {
    question: "What if I don’t have a bike or vehicle of my own?",
    answer:
      'We partner with rental providers in many cities. You can lease a scooter or bike at a discounted rate through our network.',
  },
  {
    question: 'Is there any driver support available if I have issues?',
    answer:
      'Yes—our in-app chat and phone support lines are available 7 days a week for delivery assistance and account questions.',
  },
  {
    question: 'Are there any fees or commissions deducted from my earnings?',
    answer:
      'A small service fee is deducted per delivery to cover platform and payment processing costs. The exact percentage is shown in the earnings breakdown.',
  },
];

function FAQ() {
  const [expanded, setExpanded] = useState<number | false>(false);

  const handleChange = (panel: number) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <section id="faqs" className="container mx-auto px-6 lg:px-12 py-16 lg:py-24">
      <h2 className="text-center text-2xl md:text-4xl font-bold mb-12">
        Frequently asked questions from drivers
      </h2>
      <div className="max-w-5xl mx-auto">
        {faqs.map((faq, idx) => (
          <Accordion
            key={faq.question}
            expanded={expanded === idx}
            onChange={handleChange(idx)}
            sx={{
              backgroundColor: '#F9FAFB',
              border: 'none', // Remove border
              borderRadius: '1rem',
              boxShadow: 'none',
              mb: 2, // Add margin bottom between items
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              aria-controls={`faq-content-${idx}`}
              id={`faq-header-${idx}`}
              sx={{
                padding: { xs: '1rem', md: '1.25rem' },
                minHeight: 'auto',
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                },
                '&.Mui-expanded': {
                  minHeight: 'auto',
                },
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              {/* Plus/minus icon on the left */}
              <span
                className="text-white text-lg font-bold transition-transform duration-300 mr-4"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '1.75em',
                  height: '1.75em',
                  minWidth: '1.75em',
                  minHeight: '1.75em',
                  background: '#F55109',
                  borderRadius: '50%',
                  flexShrink: 0,
                  fontSize: '1.15em',
                  lineHeight: 1,
                  userSelect: 'none',
                }}
              >
                {expanded === idx ? '−' : '+'}
              </span>
              <span className="font-medium text-gray-800 md:text-lg flex-1">
                {faq.question}
              </span>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: { xs: '0 1rem 1rem 1rem', md: '0 1.25rem 1.25rem 1.25rem' },
                backgroundColor: '#fff',
                borderTop: 'none', // Remove border
              }}
            >
              <div className="text-gray-600 text-sm md:text-base leading-relaxed">
                {faq.answer}
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </section>
  );
}

export default FAQ;
